import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAppInsightsContext, useTrackEvent } from '@microsoft/applicationinsights-react-js';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import { fetchUserData } from '../../actions/user';
import { getProfilePercentage } from '../../actions/user';
import {
  clearTimesheet, getTimeExpenseValidation,
  getTandEPendingCounts, getTandECountsContractor
} from '../../actions/timesheet';
import { getTimesheets } from '../../actions/timesheets';
import { clearExpense } from '../../actions/expense';
import { getExpenses } from '../../actions/expenses';
import { getGlobalNotification } from '../../actions/appNotifications';
import { getOptionsForFilters } from '../../actions/filtersOptionsFromApi';
import { getCounters } from '../../actions/jobs';
import { getCookie } from '../../utils/helpers';
import configData from '../../config.json';
import { idTokenClaims, TNE_SORT_BY } from '../../utils/enums';
import { globalPageSize, appAreas } from '../../utils/constants';
import DashboardSummaryBar from '../../Components/common/DashboardSummaryBar/DashboardSummaryBar';
import TimesheetsCard from '../../Components/common/Cards/TimesheetsCard/TimesheetsCard';
import ExpensesCard from '../../Components/common/Cards/ExpensesCard/ExpensesCard';
import OnboardingCard from '../../Components/common/Cards/OnboardingCard/OnboardingCard';
import MyProfileCard from '../../Components/common/Cards/MyProfileCard/MyProfileCard';
import NotificationsCard from '../../Components/common/Cards/DashboardNotificationsCard/NotificationsCard';
import JobsCard from '../../Components/common/Cards/JobsCard/JobsCard';

const AVATAR_COLORS = ['#0094B3', '#7C3AED', '#059669', '#DB2777', '#D97706', '#2563EB'];
function avatarColor(seed) {
  const str = String(seed ?? '');
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) hash = (hash * 31 + str.charCodeAt(i)) % AVATAR_COLORS.length;
  return AVATAR_COLORS[hash];
}
function initialsFromName(fullName) {
  const parts = (fullName ?? '').trim().split(/\s+/);
  return `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase() || '—';
}

export function Dashboard() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { accounts } = useMsal();
  const appInsights = useAppInsightsContext();
  const userId = localStorage.getItem('UserId');

  const user = useSelector((s) => s?.user?.profile);
  const accessTo = useSelector((s) => s.accessToParts);

  const account = accounts?.[0];
  console.log('Dashboard.jsx: account:', account);
  const givenName = account?.idTokenClaims?.given_name ?? account?.name?.split(' ')[0] ?? '';
  const fullName = `${givenName ?? ''} ${account?.idTokenClaims?.family_name ?? ''}`;
  const company = account?.idTokenClaims?.company ?? '';

  const isApprover = accessTo.tAndEApprover;
  const isContractor = accessTo.tAndEContractor;
  const hasOnboarding = accessTo.onboarding;
  const isTandE = isApprover || isContractor;

  const trackEvent = useTrackEvent(
    appInsights,
    `dashboard view userId: ${userId}`,
    accessTo.onboarding
  );

  useEffect(() => {
    dispatch(clearTimesheet());
    dispatch(clearExpense());
    dispatch(getTimeExpenseValidation('', ''));
    dispatch(getOptionsForFilters(appAreas.TIMESHEETS, isApprover, ''));
    dispatch(getOptionsForFilters(appAreas.EXPENSES, isApprover, ''));
    dispatch(getGlobalNotification());
    dispatch(getCounters());
    if (userId && !user) dispatch(fetchUserData(userId));
    dispatch(getProfilePercentage());

    const claims = account?.idTokenClaims;
    if (claims) {
      trackEvent({
        props: {
          user: user?.Id || userId,
          cookieValue: getCookie(configData.TIFO_ONBOARDING_KEY),
          onboarding: claims[idTokenClaims.IS_TIFO_USER],
          tAndEContractor: claims[idTokenClaims.IS_TNE_CONTRACTOR],
          tAndEApprover: claims[idTokenClaims.IS_TNE_APPROVER],
        },
      });
    }
  }, []);

  useEffect(() => {
    if (isApprover) dispatch(getTandEPendingCounts());
    if (isContractor) dispatch(getTandECountsContractor());
  }, [isApprover, isContractor]);

  useEffect(() => {
    if (!isTandE) return;
    dispatch(
      getTimesheets(
        userId, 1, globalPageSize,
        '', '', '', '', '',
        { FirstName: '', LastName: '' },
        [TNE_SORT_BY.PERIOD],
        t, isApprover,
        null
      )
    );
  }, [userId, isTandE, isApprover]);

  useEffect(() => {
    if (!isTandE) return;
    dispatch(
      getExpenses(
        userId, 1, globalPageSize,
        '', '', '', '', '',
        { FirstName: '', LastName: '' },
        [TNE_SORT_BY.PERIOD],
        t, isApprover,
        null
      )
    );
  }, [userId, isTandE, isApprover]);

  return (
    <div className="flex flex-col min-h-0 bg-[#F0F4F7]">
      <div className="w-full max-w-7xl mx-auto px-6 xl:px-8">

        <div className="flex items-center gap-3 pt-4 pb-1 md:pt-5">
          {accounts?.length > 0 && (
            <span
              className="flex md:hidden items-center justify-center w-10 h-10 rounded-full text-white text-[14px] font-bold shrink-0"
              style={{ backgroundColor: avatarColor(userId ?? fullName) }}
            >
              {initialsFromName(fullName)}
            </span>
          )}
          {accounts?.length > 0 && (
            <h2 className="text-[19px] md:text-[22px] font-bold text-[#022D41] leading-tight m-0">
              Hi, {givenName}
            </h2>
          )}
        </div>

        <div className="pb-4">
          <p className="text-[14px] md:text-[16px] text-[#6B7A85] mt-0 md:mt-1 m-0 leading-relaxed">
            {company
              ? <>Here's what needs your attention on{' '}
                <span className="text-[#0094B3] font-semibold">{company}</span> today.
              </>
              : "Here's what needs your attention today."
            }
          </p>
        </div>

      </div>

      {isTandE && (
        <div className="w-full max-w-7xl mx-auto px-6 xl:px-8">
          <DashboardSummaryBar noPadding />
        </div>
      )}

      <div className="w-full max-w-7xl mx-auto px-6 xl:px-8 pb-8 pt-2">
        <div className="flex flex-col lg:flex-row gap-5 items-start">
          <div className="flex flex-col gap-5 flex-2 min-w-0 w-full">
            {isTandE && <TimesheetsCard animDelay={0} />}
            {isTandE && <ExpensesCard animDelay={60} />}
            {!isApprover && <JobsCard animDelay={120} />}
          </div>
          <div className="flex flex-col gap-5 w-full lg:w-[320px] xl:w-90 shrink-0">
            {hasOnboarding && <OnboardingCard animDelay={80} />}
            {!isApprover && <MyProfileCard animDelay={140} />}
            <NotificationsCard animDelay={200} />
          </div>
        </div>
      </div>
    </div>
  );
}